// The Help pane (System context PR B): the Insights drawer (ChatDrawer →
// SidePaneChat), titled Help and scoped to the section on screen, with a
// "Show me around" menu, a greeting and the section's starter questions as
// pills before the first message and, during a tour, the step card above the
// composer. Drawn
// only; the conversation and the tour live in help/HelpProvider.
//
// It is visibly apart from the page (Adam, 14 Oct): an info-blue left edge
// as thick as the header's gold rule, a shadow onto the page, and the same
// blue around its composer. The step card has no screenshot: the page itself
// is the picture now. It says what the step did ("Did: …") and what Next
// will do ("Next: …"), and never grows past a third of the window.
//
// The drawer is persistent, not modal: the person keeps using the page, and
// the outline finds the page's controls. data-help-pane keeps the outline
// off the pane's own buttons; data-voice="off" keeps the screen tools off
// them.
import React, { useState } from 'react'
import { Box, Button, ListSubheader, Menu, MenuItem, Typography } from '@mui/material'
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined'
import ChatDrawer from '../chat/ChatDrawer'
import type { ChatMessage } from '../chat/types'
import type { TourRun } from '../../help/tourRuntime'

export interface HelpPaneStep {
  name: string
  number: number
  total: number
  featureName?: string
  say?: string
  /** What the step's action did on screen. */
  did?: string
  /** What Next will do: the next step's action label. */
  nextLabel?: string
  /** Text the person may send to the page's agent. Only their click sends it. */
  type?: string
  /** The step's page is loading. */
  navigating: boolean
}

/** A group of tours in the Show me around menu. */
export interface TourChoiceGroup {
  heading: string
  tours: TourRun[]
}

export interface HelpPaneProps {
  sectionTitle?: string
  /** Starter questions as pills above the composer; a click sends one. The provider passes them until the first message. */
  starters?: string[]
  /** "Good morning, Adam. <the section's opening line>", the first row. The provider passes it with the pills. */
  greeting?: string
  messages: ChatMessage[]
  isLoading: boolean
  error: string | null
  /** Why the person cannot ask here, when they cannot. */
  disabledReason?: string
  onSend: (text: string) => void | Promise<void>
  onReset: () => void
  onClose: () => void
  renderMessage: (message: ChatMessage, index: number) => React.ReactNode | undefined
  tourChoices: TourChoiceGroup[]
  onStartTour: (tour: TourRun) => void
  tour?: HelpPaneStep
  onNext: () => void
  onBack: () => void
  onEndTour: () => void
  onSendToAgent: (text: string) => void
}

const PAPER_PROPS = { 'data-help-pane': '', 'data-voice': 'off' }

function StepCard({ step, busy, onNext, onBack, onEndTour, onSendToAgent }: {
  step: HelpPaneStep
  busy: boolean
  onNext: () => void
  onBack: () => void
  onEndTour: () => void
  onSendToAgent: (text: string) => void
}) {
  const last = step.number === step.total
  return (
    <Box
      role="region"
      aria-label="Tour step"
      sx={{
        mx: 2,
        mb: 1,
        p: 1.5,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxHeight: '35vh',
        overflowY: 'auto',
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {step.name} · Step {step.number} of {step.total}
        {step.navigating ? ' · Opening the page…' : ''}
      </Typography>
      {step.featureName && <Typography variant="subtitle2">{step.featureName}</Typography>}
      {step.say && <Typography variant="body2">{step.say}</Typography>}
      {step.did && (
        <Typography variant="body2">
          <Box component="span" sx={{ fontWeight: 600 }}>Did:</Box> {step.did}
        </Typography>
      )}
      {step.nextLabel && !last && (
        <Typography variant="body2" color="text.secondary">
          <Box component="span" sx={{ fontWeight: 600 }}>Next:</Box> {step.nextLabel}
        </Typography>
      )}
      {step.type && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic', flex: 1, minWidth: 0 }}>
            “{step.type}”
          </Typography>
          <Button size="small" color="info" variant="outlined" onClick={() => onSendToAgent(step.type!)}>
            Send to the agent
          </Button>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button size="small" color="info" onClick={onEndTour}>End tour</Button>
        <Button size="small" color="info" onClick={onBack} disabled={busy || step.number === 1}>Back</Button>
        <Button size="small" color="info" variant="contained" onClick={onNext} disabled={busy}>
          {last ? 'Finish' : 'Next'}
        </Button>
      </Box>
    </Box>
  )
}

function ShowMeAround({ choices, disabled, onStart }: { choices: TourChoiceGroup[]; disabled: boolean; onStart: (t: TourRun) => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const groups = choices.filter((g) => g.tours.length > 0)
  if (groups.length === 0) return null
  return (
    <>
      <Button
        size="small"
        color="info"
        startIcon={<ExploreOutlinedIcon fontSize="small" />}
        disabled={disabled}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
      >
        Show me around
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} slotProps={{ paper: { sx: { maxHeight: 420 } } }}>
        {groups.flatMap((g) => [
          <ListSubheader key={`h-${g.heading}`}>{g.heading}</ListSubheader>,
          ...g.tours.map((t) => (
            <MenuItem
              key={t.id}
              onClick={() => {
                setAnchor(null)
                onStart(t)
              }}
            >
              {t.name}
            </MenuItem>
          )),
        ])}
      </Menu>
    </>
  )
}

const HelpPane: React.FC<HelpPaneProps> = ({
  sectionTitle,
  starters,
  greeting,
  messages,
  isLoading,
  error,
  disabledReason,
  onSend,
  onReset,
  onClose,
  renderMessage,
  tourChoices,
  onStartTour,
  tour,
  onNext,
  onBack,
  onEndTour,
  onSendToAgent,
}) => (
  <ChatDrawer
    open
    variant="persistent"
    paperProps={PAPER_PROPS}
    accent
    title="Help"
    scopeLabel={sectionTitle}
    greeting={greeting}
    messages={messages}
    examplePrompts={starters?.length ? starters : undefined}
    onSendMessage={(_model, text) => onSend(text)}
    onResetChat={onReset}
    onClose={onClose}
    isLoading={isLoading}
    loadingTitle="Looking into it…"
    error={disabledReason ?? error}
    placeholder={sectionTitle ? `Ask how ${sectionTitle} works…` : 'Ask how this page works…'}
    showAdvancedMode={false}
    personas={[]}
    disabled={!!disabledReason}
    renderMessage={renderMessage}
    additionalControls={
      <ShowMeAround choices={tourChoices} disabled={!!disabledReason || isLoading} onStart={onStartTour} />
    }
    inputHelper={
      tour ? (
        <StepCard step={tour} busy={isLoading} onNext={onNext} onBack={onBack} onEndTour={onEndTour} onSendToAgent={onSendToAgent} />
      ) : undefined
    }
  />
)

export default HelpPane
